'use client';

import type { ButtonHTMLAttributes, HTMLAttributes } from 'react';
import React, { forwardRef } from 'react';
import clsx from 'clsx';
import { Tooltip } from 'components/Tooltip';
import { AnimatePresence, motion } from 'framer-motion';
import type { ThemeSpaces } from '@frameio/vapor-theme';
import { theme } from '@frameio/vapor-theme';
import styles from './InputErrorAdornment.module.css';
import type { TextFieldErrorTypes } from './types';

export type InputErrorAdornmentProps = {
  /** The error message to display */
  error?: string;
  /** The variant of the error message to display */
  errorVariant: TextFieldErrorTypes;
  /** The spacing to apply to the error adornment */
  marginRight?: ThemeSpaces;
};

const TOOLTIP_OFFSET = [0, 10] as const;

const DEFAULT_MARGIN_RIGHT: ThemeSpaces = 8;

/** Local CSS variable carrying the resolved `marginRight` spacing value. */
const MARGIN_RIGHT_VAR = '--vapor-text-field-input-error-margin-right';

export function InputErrorAdornment({
  error,
  errorVariant,
  marginRight = DEFAULT_MARGIN_RIGHT,
}: InputErrorAdornmentProps) {
  const adornmentStyle = {
    [MARGIN_RIGHT_VAR]: theme.spaces[marginRight],
  } as React.CSSProperties;

  const content =
    errorVariant === 'tooltip' ? (
      <Tooltip
        delayIn={50}
        offset={TOOLTIP_OFFSET}
        placement="top"
        skipAriaDescription
        title={error}
      >
        <AlertButton aria-label={error} style={adornmentStyle} />
      </Tooltip>
    ) : (
      <AlertIcon aria-hidden style={adornmentStyle} />
    );

  return (
    <AnimatePresence initial={false}>
      {error && (
        <motion.div
          animate={{
            opacity: 1,
            scale: 1,
            transition: {
              opacity: {
                duration: 0.2,
                ease: theme.transitions.curves.primary,
              },
              scale: { type: 'spring', stiffness: 281, damping: 22 },
            },
          }}
          className={styles.motionBounce}
          exit={{
            opacity: 0,
            scale: 0,
            transition: {
              opacity: {
                duration: 0.2,
                ease: theme.transitions.curves.primary,
              },
              scale: { duration: 0.2, ease: theme.transitions.curves.primary },
            },
          }}
          initial={{ scale: 0, opacity: 0 }}
        >
          {content}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function AlertGlyph() {
  return (
    <svg
      aria-hidden
      fill="none"
      height="16"
      viewBox="0 0 16 16"
      width="16"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8 15.0566C11.6372 15.0566 14.6333 12.0542 14.6333 8.41699C14.6333 4.77979 11.6309 1.78369 8 1.78369C4.36279 1.78369 1.3667 4.77979 1.3667 8.41699C1.3667 12.0542 4.36279 15.0566 8 15.0566ZM8 13.5649C5.1499 13.5649 2.86475 11.2671 2.86475 8.41699C2.86475 5.56689 5.14355 3.27539 8 3.27539C10.8501 3.27539 13.1416 5.56689 13.1479 8.41699C13.1479 11.2671 10.8501 13.5649 8 13.5649ZM8 9.39453C8.37451 9.39453 8.59668 9.18506 8.61572 8.78516L8.71094 5.9541C8.72998 5.5415 8.42529 5.25586 7.99365 5.25586C7.55566 5.25586 7.25732 5.5415 7.27637 5.94775L7.37158 8.7915C7.38428 9.17871 7.60645 9.39453 8 9.39453ZM8 11.5337C8.43799 11.5337 8.79346 11.2354 8.79346 10.8037C8.79346 10.3784 8.44434 10.0801 8 10.0801C7.54932 10.0801 7.2002 10.3784 7.2002 10.8037C7.2002 11.2354 7.55566 11.5337 8 11.5337Z"
        fill={theme.colors.icon.interactive.error}
        fillOpacity="0.8"
      />
    </svg>
  );
}

type AlertIconProps = HTMLAttributes<HTMLDivElement>;

function AlertIcon(props: AlertIconProps) {
  return (
    <div className={clsx(styles.iconLayout, styles.iconContainer)} {...props}>
      <AlertGlyph />
    </div>
  );
}

type AlertButtonProps = ButtonHTMLAttributes<HTMLButtonElement>;

const AlertButton = forwardRef<HTMLButtonElement, AlertButtonProps>(
  (props, ref) => (
    <button
      ref={ref}
      className={clsx(styles.iconLayout, styles.iconButton)}
      type="button"
      {...props}
    >
      <AlertGlyph />
    </button>
  )
);
AlertButton.displayName = 'AlertButton';
